import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/Button";
import type { Shift } from "../../types";

export interface ShiftFormValues {
  name: string;
  start_time: string;
  end_time: string;
  is_active: boolean;
}

interface ShiftFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: ShiftFormValues) => Promise<void> | void;
  initialValues?: Shift | null;
  isSubmitting?: boolean;
}

const defaultValues: ShiftFormValues = {
  name: "",
  start_time: "12:00",
  end_time: "14:00",
  is_active: true,
};

const ShiftForm: React.FC<ShiftFormProps> = ({
  open,
  onClose,
  onSubmit,
  initialValues = null,
  isSubmitting = false,
}) => {
  const [values, setValues] = useState<ShiftFormValues>(defaultValues);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      if (initialValues) {
        setValues({
          name: initialValues.name,
          start_time: initialValues.start_time,
          end_time: initialValues.end_time,
          is_active: initialValues.is_active,
        });
      } else {
        setValues(defaultValues);
      }
      setError(null);
    }
  }, [open, initialValues]);

  const handleChange =
    (field: keyof ShiftFormValues) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value, type, checked } = event.target;
      setValues((prev) => ({
        ...prev,
        [field]: type === "checkbox" ? checked : value,
      }));
    };

  const minutesFromTime = (time: string) => {
    const [hours, minutes] = time.split(":").map(Number);
    return hours * 60 + minutes;
  };

  const validate = (data: ShiftFormValues) => {
    if (!data.name.trim()) {
      return "Το όνομα της βάρδιας είναι απαραίτητο.";
    }
    if (!data.start_time || !data.end_time) {
      return "Οι ώρες έναρξης και λήξης είναι απαραίτητες.";
    }

    if (minutesFromTime(data.start_time) >= minutesFromTime(data.end_time)) {
      return "Η ώρα λήξης πρέπει να είναι μετά την ώρα έναρξης.";
    }

    return null;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedValues: ShiftFormValues = {
      ...values,
      name: values.name.trim(),
    };

    const validationError = validate(trimmedValues);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      await onSubmit(trimmedValues);
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : "Παρουσιάστηκε σφάλμα κατά την αποθήκευση."
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent onOpenAutoFocus={(event) => event.preventDefault()}>
        <form className="space-y-5" onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {initialValues ? "Επεξεργασία βάρδιας" : "Νέα βάρδια"}
            </DialogTitle>
            <DialogDescription>
              Καθορίστε διάστημα εξυπηρέτησης πελατών.
            </DialogDescription>
          </DialogHeader>

          <label className="space-y-1 text-sm text-card-foreground w-full">
            <span>Όνομα βάρδιας</span>
            <input
              value={values.name}
              onChange={handleChange("name")}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              placeholder="π.χ. Μεσημεριανή"
              required
            />
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="space-y-1 text-sm text-card-foreground">
              <span>Ώρα έναρξης</span>
              <input
                type="time"
                value={values.start_time}
                onChange={handleChange("start_time")}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                required
              />
            </label>

            <label className="space-y-1 text-sm text-card-foreground">
              <span>Ώρα λήξης</span>
              <input
                type="time"
                value={values.end_time}
                onChange={handleChange("end_time")}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                required
              />
            </label>
          </div>

          <label className="flex items-center gap-2 text-sm text-card-foreground">
            <input
              type="checkbox"
              checked={values.is_active}
              onChange={handleChange("is_active")}
              className="h-4 w-4 rounded border border-input"
            />
            <span>Ενεργή βάρδια</span>
          </label>

          {error && (
            <div className="rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Ακύρωση
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Αποθήκευση..." : "Αποθήκευση"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ShiftForm;

